import { CreateAdvisorProfileErrorType } from "./types";
import { dayjs, EMAIL_REGEXP } from "./utils";
import { ValidationError } from "@gsas/gsas-sdk";
const NAME_VALIDATION_REGEXP: RegExp = /^[a-zA-Z][a-zA-Z\s-.']{1,}$/;

export const userProfileValidations = {
    countryOfCitizenship1: (val?: string) => {
        if (!val) return "Citizenship is required";
    },
    dateOfBirth: (val?: string) => {
        if (!val) return "Date of birth is required";
        const dob = dayjs(val)
        if (dayjs().diff(dob, "year") < 18)
            return "Cannot be a minor"
        if (dayjs().subtract(dob.year(), "year").year() >= 110)
            return "Cannot be that old"
        if (dob.isAfter(dayjs()))
            return "Cannot be in the future"
    },
    workTelephone: (val?: string) => {
        if (!val) return "Primary phone number is required";
        if (val.length > 15) return "Primary phone number must not exceed 15 characters";
        if (val && val.length < 10) return "Secondary phone number must be at least 10 characters";
    },
    homeTelephone: (val?: string) => {
        if (val && val.length > 15) return "Secondary phone number must not exceed 15 characters";
        if (val && val.length < 10) return "Secondary phone number must be at least 10 characters";
    },
    email1: (val?: string) => {
        if (!val) return "Email is required";
        if (val && !EMAIL_REGEXP.test(val))
            return "Email is invalid";
    },
    email2: (val?: string) => {
        if (val && !EMAIL_REGEXP.test(val))
            return "Secondary email is invalid";
    },
    firstName: (val?: string) => {
        if (!val) return "First name is required";
        if (val && !NAME_VALIDATION_REGEXP.test(val)) return "First name is invalid";
    },
    lastName: (val?: string) => {
        if (!val) return "Last name is required";
        if (val && !NAME_VALIDATION_REGEXP.test(val)) return "Last name is invalid";
    },
    middleName: (val?: string) => {
        if (val && !NAME_VALIDATION_REGEXP.test(val)) return "Middle name is invalid";
    },
    loginID: (val?: string, isCompleted?: boolean, loginID?: string[]) => {
        if (isCompleted) return;
        if (val && val !== val.toLowerCase()) return "Username must be in lowercase";
        if (!val) return "Username is required";
        if (val.length < 8) return "Username must be at least 8 characters";
        if (val.length > 32) return "Username must not exceed 32 characters";

        if (val && loginID && loginID?.includes(val)) {
            return "Cannot enter the same username for multiple users";
        }
    },
    tid: (val?: string) => {
        if (!val) return "SSN or tax id number is required";
        if (!/^\d{9}$/.test(val)) return "SSN or tax id number has an incorrect format";
    },
    hAddress: {
        address1: (val?: string) => {
            const poBoxRe: RegExp = /\b\s*\d*\s*((P(OST)?.?\s*(O|o)(FF(ICE)?)?.?\s*(B(IN|OX))?)|B(IN|OX))\s*\d*\b/i;
            if (!val) return "Address line 1 is required";
            if (val && poBoxRe.test(val)) return "P.O. Box not permitted in address line 1";
        },
        address2: (val?: string) => {
            const poBoxRe: RegExp = /\b\s*\d*\s*((P(OST)?.?\s*(O|o)(FF(ICE)?)?.?\s*(B(IN|OX))?)|B(IN|OX))\s*\d*\b/i;
            if (val && poBoxRe.test(val)) return "P.O. Box not permitted in address line 2";
        },
        city: (val?: string) => {
            if (!val) return "City is required";
        },
        state: (val?: string) => {
            if (!val) return "State is required";
        },
        zip: (val?: string) => {
            if (!val) return "Zip is required";
        }

    }
};

function validatePrimaryAddress(currUserTask: any) {
    const errs: CreateAdvisorProfileErrorType["hAddress"] = {};
    const address1 = userProfileValidations.hAddress.address1(currUserTask?.hAddress?.address1);
    const address2 = userProfileValidations.hAddress.address2(currUserTask?.hAddress?.address2);
    const city = userProfileValidations.hAddress.city(currUserTask?.hAddress?.city);
    const state = userProfileValidations.hAddress.state(currUserTask?.hAddress?.state);
    const zip = userProfileValidations.hAddress.zip(currUserTask?.hAddress?.zip);
    if (address1) errs.address1 = address1;
    if (address2) errs.address2 = address2;
    if (city) errs.city = city;
    if (state) errs.state = state;
    if (zip) errs.zip = zip;
    return errs;
}

export function validateClientInformationForm(currUserTask: any) {
    const errs: CreateAdvisorProfileErrorType = {};
    const firstName = userProfileValidations.firstName(currUserTask?.firstName);
    const middleName = userProfileValidations.middleName(currUserTask?.middleName);
    const lastName = userProfileValidations.lastName(currUserTask?.lastName);
    const email1 = userProfileValidations.email1(currUserTask?.email1);
    const email2 = userProfileValidations.email2(currUserTask?.email2);
    const workTelephone = userProfileValidations.workTelephone(currUserTask?.workTelephone);
    const loginID = userProfileValidations.loginID(currUserTask?.loginID);
    const homeTelephone = userProfileValidations.homeTelephone(currUserTask?.homeTelephone);
    const dateOfBirth = userProfileValidations.dateOfBirth(currUserTask?.dateOfBirth);
    const tid = userProfileValidations.tid(currUserTask?.tid);
    const hAddress = validatePrimaryAddress(currUserTask);
    if (firstName) errs.firstName = firstName;
    if (middleName) errs.middleName = middleName;
    if (lastName) errs.lastName = lastName;
    if (loginID) errs.loginID = loginID;
    if (email1) errs.email1 = email1;
    if (email2) errs.email2 = email2;
    if (!currUserTask.isThirdPartyAgent) {
        if (tid) errs.tid = tid;
        if (workTelephone) errs.workTelephone = workTelephone;
        if (homeTelephone) errs.homeTelephone = homeTelephone;
        if (dateOfBirth) errs.dateOfBirth = dateOfBirth;
        if (loginID) errs.loginID = loginID;
        if (Object.values(hAddress).length)
            errs.hAddress = hAddress;
    }
    return errs;
}

export interface ErrorDict {
    [path: string]: ValidationError | undefined;
}

export function userProfileErrors(updatedUserProfileValues: any): ErrorDict {
    const errors = validateUserTask(updatedUserProfileValues);
    return errors;
}

function validateUserTask(currUserTask: any) {

    const ret: ErrorDict = {};
    const poBoxRe: RegExp = /\b\s*\d*\s*((P(OST)?.?\s*(O|o)(FF(ICE)?)?.?\s*(B(IN|OX))?)|B(IN|OX))\s*\d*\b/i;
    const NAME_VALIDATION_REGEXP: RegExp = /^[a-zA-Z][a-zA-Z\s-.']{1,}$/;

    if (!currUserTask?.firstName) {
        ret[`firstName`] = {displayMessage: 'First name is required'}
        if (currUserTask?.firstName && !NAME_VALIDATION_REGEXP.test(currUserTask.firstName)) {
            ret[`firstName`] = {displayMessage: 'First name is invalid'}
        }
    }
    if (!currUserTask?.middleName) {
        if (currUserTask?.middleName && !NAME_VALIDATION_REGEXP.test(currUserTask.middleName)) {
            ret[`middleName`] = {displayMessage: 'Middle name is invalid'}
        }
    }
    if (!currUserTask?.lastName) {
        ret[`lastName`] = {displayMessage: 'Last name is required'}
        if (currUserTask?.lastName && !NAME_VALIDATION_REGEXP.test(currUserTask.lastName)) {
            ret[`lastName`] = {displayMessage: 'Last name is invalid'}
        }
    }
    if (!currUserTask?.email1) {
        ret[`email1`] = {displayMessage: 'Email is required'}
        if (currUserTask?.email1 && !EMAIL_REGEXP.test(currUserTask.email1)) {
            ret[`email1`] = {displayMessage: 'Email is invalid'}
        }
    } 
    if (!currUserTask?.loginID) {
        ret[`username`] = {displayMessage: 'Username is required'}
    } else if (currUserTask?.loginID.length < 8) {
        ret[`username`] = {displayMessage: 'Username must be at least 8 characters'}
    }
    if (currUserTask?.email2 && !EMAIL_REGEXP.test(currUserTask.email2)) {
        ret[`email2`] = {displayMessage: 'Recovery email is invalid'}
    }
    if (!currUserTask?.dateOfBirth) {
        ret[`dateOfBirth`] = {displayMessage: 'Date of birth is required'};
    }
    if (currUserTask?.dateOfBirth && dayjs(currUserTask?.dateOfBirth).isAfter(dayjs())) {
        ret[`dateOfBirth`] = {displayMessage: 'Date of birth cannot be in future'};
    }
    if (currUserTask?.dateOfBirth && dayjs(currUserTask?.dateOfBirth).isBefore('1900-01-01', 'month')) {
        ret[`dateOfBirth`] = {displayMessage: 'Date of birth cannot be before 1900'};
    }
    if (currUserTask?.dateOfBirth && dayjs().diff(currUserTask?.dateOfBirth, "year") < 18) {
        ret[`dateOfBirth`] = {displayMessage: 'Client should be older than 18'};
    }
    if (!currUserTask?.workTelephone) {
        ret[`workTelephone`] = {displayMessage: 'Primary phone number is required'}
    } else if (!/^\d*$/.test(currUserTask.workTelephone)) {
        ret[`workTelephone`] = {displayMessage: 'Primary phone number must be numeric'};
    } else if (currUserTask.workTelephone.length < 10 || currUserTask.workTelephone.length > 15) {
        ret[`workTelephone`] = {displayMessage: 'Primary phone number is invalid'};
    }
    if (!currUserTask?.homeTelephone) {
        ret[`homeTelephone`] = {displayMessage: 'Secondary phone number is required'}
    } else if (!/^\d*$/.test(currUserTask.homeTelephone)) {
        ret[`homeTelephone`] = {displayMessage: 'Secondary phone number must be numeric'};
    } else if (currUserTask.homeTelephone.length < 10 || currUserTask.homeTelephone.length > 15) {
        ret[`homeTelephone`] = {displayMessage: 'Secondary phone number is invalid'};
    }
    if (!currUserTask?.hAddress?.address1) {
        ret[`address1`] = {displayMessage: 'Address line 1 is required'}
    }
    if (currUserTask?.hAddress?.address1 && poBoxRe.test(currUserTask?.hAddress?.address1)) {
        ret[`address1`] = {displayMessage: 'P.O. Box not permitted'}
    }
    if (currUserTask?.hAddress?.address2 && poBoxRe.test(currUserTask?.hAddress?.address2)) {
        ret[`address2`] = {displayMessage: 'P.O. Box not permitted'}
    }
    if (!currUserTask?.hAddress?.city) {
        ret[`city`] = {displayMessage: 'Address city is required'}
    }
    if (!currUserTask?.hAddress?.state) {
        ret[`state`] = {displayMessage: 'Address state is required'}
    }
    if (!currUserTask?.hAddress?.zip) {
        ret[`zip`] = {displayMessage: 'Address zip is required'}
    } else if (!/^\d{5}(-\d{4})?$/.test(currUserTask.hAddress.zip)) {
        ret[`zip`] = {displayMessage: 'Primary address zip format is invalid'};
    }


    /*
    Client's Personal Information
     */
    if (!currUserTask?.dateOfBirth) {
        ret[`dateOfBirth`] = {displayMessage: 'Date of birth is required'};
    }
    if (currUserTask?.dateOfBirth && dayjs(currUserTask?.dateOfBirth).isAfter(dayjs())) {
        ret[`dateOfBirth`] = {displayMessage: 'Date of birth cannot be in future'};
    }
    if (currUserTask?.dateOfBirth && dayjs(currUserTask?.dateOfBirth).isBefore('1900-01-01', 'month')) {
        ret[`dateOfBirth`] = {displayMessage: 'Date of birth cannot be before 1900'};
    }
    if (currUserTask?.dateOfBirth && dayjs().diff(currUserTask?.dateOfBirth, "year") < 18) {
        ret[`dateOfBirth`] = {displayMessage: 'Client should be older than 18'};
    }
    if (!currUserTask?.tid) {
        ret[`tid`] = {displayMessage: 'SSN or Tax ID is required'}
    } else if (!/^\d{9}$/.test(currUserTask.tid)) {
        ret[`tid`] = {displayMessage: 'SSN or Tax ID has an incorrect format'}
    }
    if (currUserTask?.dependentCount && (currUserTask?.dependentCount < 0 || !Number.isInteger(currUserTask?.dependentCount))) {
        ret[`dependentCount`] = {displayMessage: 'Number of Dependents should be a whole number'}
    }

    return ret;
}